/**
 * Database error classification for Sentry observability.
 *
 * The Result pattern swallows Postgres errors (services return
 * `{ success: false, error }` instead of throwing), so Sentry never sees
 * them — even though a missing GRANT or schema drift is ALWAYS a code/deploy
 * bug that silently returns empty reads / no-op writes to users.
 *
 * `isAlwaysBugDbError` is the single source of truth for "this SQLSTATE always
 * means a bug, never a runtime authorization outcome". Call sites use it to
 * decide whether to fire a Sentry capture side-effect BEFORE returning the
 * ServiceError unchanged. Keep the rule here so every seam classifies identically.
 */

/**
 * SQLSTATEs that ALWAYS indicate a code/deploy bug (missing grant, schema
 * drift), never a runtime authorization outcome.
 *
 * - `42P01` undefined_table
 * - `42883` undefined_function
 * - `42804` datatype_mismatch (RPC return-type drift)
 * - `42703` undefined_column
 *
 * `42501` (insufficient_privilege) is handled separately because it is
 * ambiguous: a MISSING GRANT is a bug, but an RLS write denial is the policy
 * doing its job — an expected authorization outcome we must NOT capture.
 */
const ALWAYS_BUG_SQLSTATES: ReadonlySet<string> = new Set([
  '42P01',
  '42883',
  '42804',
  '42703',
]);

/**
 * PostgREST codes that ALWAYS indicate a code/deploy bug, used by the
 * transport reporter (`lib/db-failure-to-sentry.ts`, BAL-1144):
 *
 * - `PGRST202` function not found in the schema cache
 * - `PGRST204` column not found in the schema cache
 * - `PGRST205` table not found in the schema cache
 */
export const ALWAYS_BUG_POSTGREST_CODES: ReadonlySet<string> = new Set([
  'PGRST202',
  'PGRST204',
  'PGRST205',
]);

/**
 * A `42501` with this message shape is a missing GRANT on a database object —
 * always a deploy bug. RLS write denials use a different message
 * (`violates row-level security policy`) and are excluded.
 */
const PERMISSION_DENIED_PATTERN =
  /permission denied for (table|view|function|sequence|schema|relation)/i;

/**
 * True when the error is one of the "always a bug" classes: a missing GRANT
 * (`42501` + `permission denied for …`) or schema drift (undefined table /
 * function / column, RPC return-type mismatch).
 *
 * Returns false for expected authorization outcomes — notably `42501` RLS
 * write denials (`violates row-level security policy`) — so Sentry is not
 * flooded with non-actionable policy denials.
 */
export function isAlwaysBugDbError(error: {
  code?: string;
  message?: string;
}): boolean {
  if (!error) return false;

  const code = error.code;
  if (!code) return false;

  // Schema drift: undefined table / function / column, RPC return-type drift.
  if (ALWAYS_BUG_SQLSTATES.has(code)) {
    return true;
  }

  // Missing GRANT (bug) vs RLS write denial (expected authorization outcome).
  if (code === '42501') {
    return PERMISSION_DENIED_PATTERN.test(error.message ?? '');
  }

  return false;
}

/**
 * Best-effort extraction of the object name from a `permission denied for
 * <kind> <name>` message, for Sentry context. Returns `undefined` when the
 * message does not carry a parseable object name.
 */
export function extractDeniedObjectName(
  message: string | undefined,
): string | undefined {
  if (!message) return undefined;
  const match = message.match(
    /permission denied for (?:table|view|function|sequence|schema|relation) (\S+)/i,
  );
  return match?.[1];
}
